<template>
  <div>
    <div class="container-fluid">
      <div class="page-title">
        <div class="row">
          <div class="col-6">
            <h3>Дашборд поддержки</h3>
          </div>
          <div class="col-6">
            <ol class="breadcrumb">
              <li class="breadcrumb-item">
                <router-link :to="{ name: 'dashboard' }">
                  <feather type="home"></feather>
                </router-link>
              </li>
              <li class="breadcrumb-item">Тикеты</li>
              <li class="breadcrumb-item active">Дашборд</li>
            </ol>
          </div>
        </div>
      </div>

      <!-- Period filter -->
      <div class="row mb-4">
        <div class="col-12 d-flex align-items-center justify-content-between">
          <b-button-group>
            <b-button
                v-for="p in periods"
                :key="p.value"
                :variant="period === p.value ? 'primary' : 'outline-primary'"
                size="sm"
                @click="setPeriod(p.value)"
            >
              {{ p.label }}
            </b-button>
          </b-button-group>
        </div>
      </div>

      <!-- Global metrics -->
      <div class="row">
        <div class="col-xl col-md-4 col-sm-6 mb-4" v-for="metric in metricsCards" :key="metric.title">
          <div class="card h-100 mb-0">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <span class="text-muted small text-uppercase font-weight-bold">{{ metric.title }}</span>
                <feather :type="metric.icon" class="text-primary" style="width: 18px; height: 18px;"></feather>
              </div>
              <h3 class="mb-0 font-weight-bold">
                <template v-if="metricsLoading">
                  <b-spinner small variant="primary"></b-spinner>
                </template>
                <template v-else>
                  {{ metric.value }}
                </template>
              </h3>
              <small class="text-muted">{{ metric.subtitle }}</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts row: Volume + Sources -->
      <div class="row">
        <div class="col-xl-8 mb-4">
          <div class="card h-100 mb-0">
            <div class="card-header d-flex align-items-center justify-content-between pb-0">
              <h5 class="mb-0">Динамика обращений</h5>
              <b-form-select
                  v-model="groupBy"
                  :options="groupByOptions"
                  size="sm"
                  style="width: 140px"
                  @change="loadVolumeChart"
              ></b-form-select>
            </div>
            <div class="card-body">
              <div v-if="volumeLoading" class="d-flex justify-content-center align-items-center" style="height: 300px">
                <b-spinner variant="primary"></b-spinner>
              </div>
              <apexchart
                  v-else
                  type="area"
                  height="300"
                  :options="volumeChartOptions"
                  :series="volumeChartSeries"
              ></apexchart>
            </div>
          </div>
        </div>

        <div class="col-xl-4 mb-4">
          <div class="card h-100 mb-0">
            <div class="card-header pb-0">
              <h5 class="mb-0">Источники</h5>
            </div>
            <div class="card-body d-flex flex-column align-items-center justify-content-center">
              <div v-if="sourcesLoading" class="d-flex justify-content-center align-items-center" style="height: 300px">
                <b-spinner variant="primary"></b-spinner>
              </div>
              <apexchart
                  v-else
                  type="donut"
                  height="300"
                  :options="sourcesChartOptions"
                  :series="sourcesChartSeries"
              ></apexchart>
            </div>
          </div>
        </div>
      </div>

      <!-- Categories + Team Performance -->
      <div class="row">
        <div class="col-xl-4 mb-4">
          <div class="card h-100 mb-0">
            <div class="card-header d-flex align-items-center justify-content-between pb-0">
              <h5 class="mb-0">Категории</h5>
              <b-form-select
                  v-model="categoryTypeId"
                  :options="categoryTypeOptions"
                  size="sm"
                  style="width: 160px"
                  @change="loadCategories"
              ></b-form-select>
            </div>
            <div class="card-body">
              <div v-if="categoriesLoading" class="d-flex justify-content-center align-items-center" style="height: 320px">
                <b-spinner variant="primary"></b-spinner>
              </div>
              <apexchart
                  v-else
                  type="bar"
                  height="320"
                  :options="categoriesChartOptions"
                  :series="categoriesChartSeries"
              ></apexchart>
            </div>
          </div>
        </div>

        <div class="col-xl-8 mb-4">
          <div class="card h-100 mb-0">
            <div class="card-header pb-0">
              <h5 class="mb-0">Производительность команды</h5>
            </div>
            <div class="card-body p-0">
              <div v-if="teamLoading" class="d-flex justify-content-center align-items-center" style="height: 300px">
                <b-spinner variant="primary"></b-spinner>
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover mb-0">
                  <thead>
                  <tr>
                    <th style="cursor: pointer; user-select: none;" @click="toggleTeamSort('username')">
                      Сотрудник <span class="text-muted small">{{ getSortIcon('username') }}</span>
                    </th>
                    <th class="text-center" style="cursor: pointer; user-select: none;" @click="toggleTeamSort('processed')">
                      Обработано <span class="text-muted small">{{ getSortIcon('processed') }}</span>
                    </th>
                    <th class="text-center" style="cursor: pointer; user-select: none;" @click="toggleTeamSort('key_actions')">
                      Ключевые действия <span class="text-muted small">{{ getSortIcon('key_actions') }}</span>
                    </th>
                    <th class="text-center" style="cursor: pointer; user-select: none;" @click="toggleTeamSort('team_accepted')">
                      Заявки команд (Пр/Отк) <span class="text-muted small">{{ getSortIcon('team_accepted') }}</span>
                    </th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="agent in sortedTeamPerformance" :key="agent.user_id">
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mr-2"
                             style="width: 32px; height: 32px; font-size: 13px; font-weight: 600;">
                          {{ getInitial(agent.username) }}
                        </div>
                        <span class="font-weight-bold">{{ agent.username }}</span>
                      </div>
                    </td>
                    <td class="text-center">{{ agent.processed }}</td>
                    <td class="text-center">
                      <b-badge variant="success">{{ agent.key_actions }}</b-badge>
                    </td>
                    <td class="text-center">
                      <span class="text-success font-weight-bold">{{ agent.entries_accepted }} </span>
                      <span class="text-muted mx-1">/</span>
                      <span class="text-danger font-weight-bold">{{ agent.team_rejected }}</span>
                    </td>
                  </tr>
                  <tr v-if="!sortedTeamPerformance.length">
                    <td colspan="5" class="text-center text-muted py-4">Нет данных за выбранный период</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {defineComponent} from 'vue'
import api from "@/api/api"
import VueApexCharts from 'vue-apexcharts'

const SOURCE_LABELS = {
  website: 'Web Виджет',
  telegram: 'Telegram',
}

export default defineComponent({
  name: "TicketsDashboard",
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      period: 'week',
      periods: [
        {value: 'day', label: 'День'},
        {value: 'week', label: 'Неделя'},
        {value: 'month', label: 'Месяц'},
        {value: 'quarter', label: 'Квартал'},
        {value: 'year', label: 'Год'},
      ],
      groupBy: 'day',
      groupByOptions: [
        {value: 'hour', text: 'По часам'},
        {value: 'day', text: 'По дням'},
      ],
      categoryTypeId: null,
      categoryTypeOptions: [
        {value: null, text: 'Все типы'},
      ],

      // Data
      metrics: null,
      volumeData: [],
      sourcesData: [],
      categoriesData: [],
      teamPerformance: [],
      teamSortKey: 'processed',
      teamSortAsc: false,

      // Loading states
      metricsLoading: false,
      volumeLoading: false,
      sourcesLoading: false,
      categoriesLoading: false,
      teamLoading: false,
    }
  },

  computed: {
    metricsCards() {
      const m = this.metrics
      if (!m) {
        return [
          {title: 'Всего обращений', value: '—', subtitle: 'за период', icon: 'inbox'},
          {title: 'Завершение (ACT)', value: '—', subtitle: 'среднее время', icon: 'clock'},
          {title: 'Реакция (IAT)', value: '—', subtitle: 'среднее время', icon: 'zap'},
          {title: 'Отклонено', value: '—', subtitle: 'за период', icon: 'x-circle'},
          {title: 'Тональность', value: '—', subtitle: 'оценка', icon: 'smile'},
        ]
      }
      return [
        {title: 'Всего обращений', value: m.total_tickets, subtitle: 'за период', icon: 'inbox'},
        {title: 'Завершение (ACT)', value: this.formatMinutes(m.act_minutes), subtitle: 'среднее время', icon: 'clock'},
        {title: 'Реакция (IAT)', value: this.formatMinutes(m.iat_minutes), subtitle: 'среднее время', icon: 'zap'},
        {title: 'Отклонено', value: m.rejected, subtitle: 'за период', icon: 'x-circle'},
        {title: 'Тональность', value: m.mood !== null ? m.mood.toFixed(1) : '—', subtitle: 'оценка 1-5', icon: 'smile'},
      ]
    },

    // Volume chart
    volumeChartOptions() {
      return {
        chart: {
          type: 'area',
          toolbar: {show: false},
          fontFamily: 'inherit',
        },
        colors: ['#7366ff', '#54ba4a', '#fc4438'],
        dataLabels: {enabled: false},
        stroke: {curve: 'smooth', width: 2},
        fill: {
          type: 'gradient',
          gradient: {shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 95, 100]},
        },
        xaxis: {
          categories: this.volumeData.map(d => d.date),
          labels: {style: {colors: '#6c757d'}},
        },
        yaxis: {labels: {style: {colors: '#6c757d'}}},
        grid: {borderColor: '#e9ecef'},
        legend: {position: 'top'},
        tooltip: {theme: 'light'},
      }
    },
    volumeChartSeries() {
      return [
        {name: 'Созданные', data: this.volumeData.map(d => d.created)},
        {name: 'Решённые', data: this.volumeData.map(d => d.resolved)},
        {name: 'Отклонённые', data: this.volumeData.map(d => d.rejected)},
      ]
    },

    // Sources chart
    sourcesChartOptions() {
      return {
        chart: {type: 'donut', fontFamily: 'inherit'},
        labels: this.sourcesData.map(d => SOURCE_LABELS[d.source] || d.source),
        colors: ['#7366ff', '#38bdf8', '#54ba4a', '#ffaa05'],
        legend: {position: 'bottom'},
        dataLabels: {
          enabled: true,
          formatter: (val) => val.toFixed(1) + '%',
        },
        tooltip: {theme: 'light'},
        plotOptions: {
          pie: {
            donut: {size: '65%'},
          },
        },
      }
    },
    sourcesChartSeries() {
      return this.sourcesData.map(d => d.count)
    },

    // Categories chart
    categoriesChartOptions() {
      return {
        chart: {type: 'bar', toolbar: {show: false}, fontFamily: 'inherit'},
        plotOptions: {
          bar: {horizontal: true, barHeight: '50%', borderRadius: 4},
        },
        colors: ['#7366ff'],
        dataLabels: {enabled: false},
        xaxis: {labels: {style: {colors: '#6c757d'}}},
        yaxis: {
          labels: {style: {colors: '#343a40', fontSize: '12px'}},
        },
        grid: {borderColor: '#e9ecef'},
        tooltip: {theme: 'light'},
      }
    },
    categoriesChartSeries() {
      return [{
        name: 'Тикетов',
        data: this.categoriesData.map(d => ({
          x: d.category_name,
          y: d.count,
        })),
      }]
    },

    sortedTeamPerformance() {
      const key = this.teamSortKey
      const asc = this.teamSortAsc
      return [...this.teamPerformance].sort((a, b) => {
        let valA = a[key]
        let valB = b[key]
        if (valA === null) valA = -1
        if (valB === null) valB = -1
        if (typeof valA === 'string') {
          return asc ? valA.localeCompare(valB) : valB.localeCompare(valA)
        }
        return asc ? valA - valB : valB - valA
      })
    },
  },

  methods: {
    toggleTeamSort(key) {
      if (this.teamSortKey === key) {
        this.teamSortAsc = !this.teamSortAsc
      } else {
        this.teamSortKey = key
        this.teamSortAsc = false
      }
    },

    getSortIcon(key) {
      if (this.teamSortKey !== key) return '↕'
      return this.teamSortAsc ? '↑' : '↓'
    },

    setPeriod(val) {
      this.period = val
      this.loadAll()
    },

    async loadAll() {
      this.loadMetrics()
      this.loadVolumeChart()
      this.loadSources()
      this.loadCategories()
      this.loadTeamPerformance()
    },

    async loadMetrics() {
      this.metricsLoading = true
      try {
        const res = await api.ticketsDashboard.getMetrics({period: this.period})
        this.metrics = res.data?.data
      } catch (e) {
        console.error(e)
      } finally {
        this.metricsLoading = false
      }
    },

    async loadVolumeChart() {
      this.volumeLoading = true
      try {
        const res = await api.ticketsDashboard.getVolumeChart({period: this.period, group_by: this.groupBy})
        this.volumeData = res.data?.data || []
      } catch (e) {
        console.error(e)
      } finally {
        this.volumeLoading = false
      }
    },

    async loadSources() {
      this.sourcesLoading = true
      try {
        const res = await api.ticketsDashboard.getSources({period: this.period})
        this.sourcesData = res.data?.data || []
      } catch (e) {
        console.error(e)
      } finally {
        this.sourcesLoading = false
      }
    },

    async loadCategories() {
      this.categoriesLoading = true
      try {
        const params = {period: this.period}
        if (this.categoryTypeId) params.type_id = this.categoryTypeId
        const res = await api.ticketsDashboard.getCategories(params)
        this.categoriesData = res.data?.data || []
      } catch (e) {
        console.error(e)
      } finally {
        this.categoriesLoading = false
      }
    },

    async loadTeamPerformance() {
      this.teamLoading = true
      try {
        const res = await api.ticketsDashboard.getTeamPerformance({period: this.period})
        this.teamPerformance = res.data?.data || []
      } catch (e) {
        console.error(e)
      } finally {
        this.teamLoading = false
      }
    },

    async loadTypeOptions() {
      try {
        const res = await api.tickets.getListTicketTypes()
        const types = (res.data?.data || []).map(t => ({value: t.id, text: t.name}))
        this.categoryTypeOptions = [{value: null, text: 'Все типы'}, ...types]
      } catch (e) {
        console.error(e)
      }
    },

    formatMinutes(val) {
      if (val === null || val === undefined) return '—'
      if (val < 60) return val.toFixed(1) + 'м'
      const hours = Math.floor(val / 60)
      const mins = Math.round(val % 60)
      return hours + 'ч ' + mins + 'м'
    },

    getInitial(name) {
      if (!name) return '?'
      return name.charAt(0).toUpperCase()
    },
  },

  mounted() {
    this.loadAll()
    this.loadTypeOptions()
  },
})
</script>
